// mcn-frontend/src/contexts/UserContext.js
// ENHANCED & FIXED - Unified Export Pattern to prevent circular dependency
// Last Updated: 2025-01-11

import React, { createContext, useState, useContext, useEffect } from 'react';
import { API_ENDPOINTS, API_BASE_URL } from '../config';

// ============================================================================
// FEATURE FLAGS
// ============================================================================
const FEATURE_FLAGS = {
  ENABLE_CHANNEL_LOADING: true // Set to true when backend endpoint is ready
};

// ============================================================================
// CONTEXT CREATION
// ============================================================================
const UserContext = createContext();

// ============================================================================
// CUSTOM HOOK - useUser (NO export keyword here)
// ============================================================================
const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
};

// ============================================================================
// USER PROVIDER COMPONENT
// ============================================================================
export const UserProvider = ({ children }) => {
  // State
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [userChannels, setUserChannels] = useState([]);
  const [error, setError] = useState(null);

  // ============================================================================
  // INITIALIZE - Check for existing session
  // ============================================================================
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchUserProfile(token);

      // Load saved selected channel
      const savedChannelId = localStorage.getItem('selectedChannelId');
      if (savedChannelId) {
        // This will be set when channels are loaded
      }
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================================================
  // FETCH USER PROFILE
  // ============================================================================
  const fetchUserProfile = async (token) => {
    try {
      const response = await fetch(API_ENDPOINTS.profile, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setCurrentUser(userData);
        setError(null);

        // Set tenant info
        if (userData.tenant) {
          setTenant(userData.tenant);
        }

        // Set permissions
        if (userData.permissions) {
          setPermissions(userData.permissions);
        }

        // Load channels if user is a channel owner or creator
        if (FEATURE_FLAGS.ENABLE_CHANNEL_LOADING && 
            (userData.role === 'CHANNEL_OWNER' || 
             userData.role === 'CREATOR' || 
             userData.role === 'CONTENT_CREATOR')) {
          loadUserChannels(userData.id);
        }
      } else {
        // Token invalid - clear storage
        localStorage.removeItem('token');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
        localStorage.removeItem('tenantId');
        localStorage.removeItem('tenantName');
        setError('Session expired. Please login again.');
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
      setError('Failed to load user profile');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // LOAD USER CHANNELS
  // ============================================================================
  const loadUserChannels = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/channels/user/${userId || currentUser?.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const channels = data.data || data; // Handle both response formats
        setUserChannels(channels);

        // Set first channel as default selected or restore saved selection
        const savedChannelId = localStorage.getItem('selectedChannelId');
        if (savedChannelId) {
          const savedChannel = channels.find(ch => ch.id === savedChannelId);
          if (savedChannel) {
            setSelectedChannel(savedChannel);
          } else if (channels.length > 0) {
            setSelectedChannel(channels[0]);
            localStorage.setItem('selectedChannelId', channels[0].id);
          }
        } else if (channels.length > 0) {
          setSelectedChannel(channels[0]);
          localStorage.setItem('selectedChannelId', channels[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading channels:', error);
    }
  };

  // ============================================================================
  // SELECT CHANNEL
  // ============================================================================
  const selectChannel = (channel) => {
    setSelectedChannel(channel);
    if (channel) {
      localStorage.setItem('selectedChannelId', channel.id);
    } else {
      localStorage.removeItem('selectedChannelId');
    }
  };

  // ============================================================================
  // LOGIN
  // ============================================================================
  const login = (user) => {
    setCurrentUser(user);
    setError(null);

    // Set tenant info
    if (user.tenant) {
      setTenant(user.tenant);
    }

    // Set permissions
    if (user.permissions) {
      setPermissions(user.permissions);
    }

    // Load channels if user is a channel owner or creator
    if (FEATURE_FLAGS.ENABLE_CHANNEL_LOADING && 
        (user.role === 'CHANNEL_OWNER' || 
         user.role === 'CREATOR' || 
         user.role === 'CONTENT_CREATOR')) {
      loadUserChannels(user.id);
    }
  };

  // ============================================================================
  // LOGOUT
  // ============================================================================
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    localStorage.removeItem('tenantId');
    localStorage.removeItem('tenantName');
    localStorage.removeItem('selectedChannelId');
    setCurrentUser(null);
    setTenant(null);
    setPermissions([]);
    setSelectedChannel(null);
    setUserChannels([]);
    setError(null);
  };

  // ============================================================================
  // UPDATE USER
  // ============================================================================
  const updateUser = (updates) => {
    setCurrentUser(prev => ({ ...prev, ...updates }));
  };

  // ============================================================================
  // PERMISSION CHECKS
  // ============================================================================
  const hasPermission = (permission) => {
    if (!currentUser) return false;
    if (currentUser.role === 'SUPER_ADMIN') return true;
    return permissions.includes(permission);
  };

  const hasAnyPermission = (permissionList) => {
    if (!currentUser) return false;
    if (currentUser.role === 'SUPER_ADMIN') return true;
    return permissionList.some(permission => permissions.includes(permission));
  };

  const hasAllPermissions = (permissionList) => {
    if (!currentUser) return false;
    if (currentUser.role === 'SUPER_ADMIN') return true;
    return permissionList.every(permission => permissions.includes(permission));
  };

  // ============================================================================
  // ROLE CHECKS
  // ============================================================================
  const hasRole = (role) => {
    if (!currentUser) return false;
    return currentUser.role === role;
  };

  const hasAnyRole = (roleList) => {
    if (!currentUser) return false;
    return roleList.includes(currentUser.role);
  };

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================
  const isAuthenticated = !!currentUser;

  const canAccessFeature = (feature) => {
    // Add feature-specific logic here
    return hasPermission(`access_${feature}`);
  };

  const getCommissionRate = () => {
    if (!currentUser) return 0;
    // Return commission rate based on role/user settings
    return currentUser.commissionRate || 0;
  };

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================
  const value = {
    // State
    currentUser,
    loading,
    tenant,
    permissions,
    selectedChannel,
    userChannels,
    error,
    isAuthenticated,

    // Actions
    login,
    logout,
    updateUser,
    fetchUserProfile,

    // Channel Management
    loadUserChannels,
    selectChannel,
    hasChannel: userChannels.length > 0,
    canUpload: !!(currentUser && 
                  (currentUser.role === 'CHANNEL_OWNER' || 
                   currentUser.role === 'CREATOR' ||
                   currentUser.role === 'CONTENT_CREATOR') && 
                  selectedChannel),

    // Permission Checks
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,

    // Role Checks
    hasRole,
    hasAnyRole,

    // Helper Functions
    canAccessFeature,
    getCommissionRate
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

// ============================================================================
// PERMISSIONS CONSTANTS
// ============================================================================
const PERMISSIONS = {
  // User Management
  VIEW_USERS: 'view_users',
  CREATE_USERS: 'create_users',
  EDIT_USERS: 'edit_users',
  DELETE_USERS: 'delete_users',
  MANAGE_ROLES: 'manage_roles',

  // Channel Management
  VIEW_CHANNELS: 'view_channels',
  CREATE_CHANNELS: 'create_channels',
  EDIT_CHANNELS: 'edit_channels',
  DELETE_CHANNELS: 'delete_channels',
  MANAGE_CHANNELS: 'manage_channels',

  // Content Management
  VIEW_CONTENT: 'view_content',
  CREATE_CONTENT: 'create_content',
  EDIT_CONTENT: 'edit_content',
  DELETE_CONTENT: 'delete_content',
  PUBLISH_CONTENT: 'publish_content',
  APPROVE_CONTENT: 'approve_content',
  REJECT_CONTENT: 'reject_content',

  // Project Management
  VIEW_PROJECTS: 'view_projects',
  CREATE_PROJECTS: 'create_projects',
  EDIT_PROJECTS: 'edit_projects',
  DELETE_PROJECTS: 'delete_projects',
  APPROVE_PROJECTS: 'approve_projects',

  // Investment Management
  VIEW_INVESTMENTS: 'view_investments',
  CREATE_INVESTMENTS: 'create_investments',
  APPROVE_INVESTMENTS: 'approve_investments',
  MANAGE_INVESTMENTS: 'manage_investments',

  // Financial Management
  VIEW_FINANCE: 'view_finance',
  MANAGE_PAYMENTS: 'manage_payments',
  VIEW_REVENUE: 'view_revenue',
  DISTRIBUTE_REVENUE: 'distribute_revenue',

  // Analytics
  VIEW_ANALYTICS: 'view_analytics',
  EXPORT_ANALYTICS: 'export_analytics',
  VIEW_REPORTS: 'view_reports',

  // Settings
  MANAGE_SETTINGS: 'manage_settings',
  MANAGE_TENANT: 'manage_tenant',
  VIEW_AUDIT_LOGS: 'view_audit_logs',

  // Support
  VIEW_SUPPORT: 'view_support',
  MANAGE_SUPPORT: 'manage_support'
};

// ============================================================================
// ROLES CONSTANTS
// ============================================================================
const ROLES = {
  // Admin Roles
  SUPER_ADMIN: 'SUPER_ADMIN',
  TENANT_ADMIN: 'TENANT_ADMIN',
  ADMIN: 'ADMIN',

  // Management Roles
  MANAGER: 'MANAGER',
  MCN_MANAGER: 'MCN_MANAGER',
  FINANCE_MANAGER: 'FINANCE_MANAGER',
  CONTENT_MANAGER: 'CONTENT_MANAGER',
  CHANNEL_MANAGER: 'CHANNEL_MANAGER',

  // Business Roles
  AGGREGATOR: 'AGGREGATOR',
  CHANNEL_OWNER: 'CHANNEL_OWNER',
  
  // Creator Roles
  CREATOR: 'CREATOR',
  CONTENT_CREATOR: 'CONTENT_CREATOR',

  // Studio Roles
  STUDIO: 'STUDIO',
  STUDIO_ADMIN: 'STUDIO_ADMIN',
  STUDIO_MEMBER: 'STUDIO_MEMBER',

  // Investment Roles
  INVESTOR: 'INVESTOR',
  PREMIUM_INVESTOR: 'PREMIUM_INVESTOR',

  // Support Roles
  SUPPORT_AGENT: 'SUPPORT_AGENT',
  SUPPORT: 'SUPPORT',

  // Analytics Roles
  ANALYTICS_VIEWER: 'ANALYTICS_VIEWER',

  // Other Roles
  MODERATOR: 'MODERATOR',
  VIEWER: 'VIEWER',
  USER: 'USER'
};

// ============================================================================
// UNIFIED EXPORTS - All at bottom to prevent hoisting issues
// ============================================================================
export default UserContext;

export { 
  useUser, 
  PERMISSIONS, 
  ROLES 
};